import * as React from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { FormLabelWrapper, PhoneInput, isValidPhoneNumber } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';

import { IFormModalWrapper, ModalToggleWrapper } from './form-modal-wrapper';
import { useEmployerPhoneNumberUpdateMutation, useIsWhatsappVerification } from './use-phone-number-otp-validation';

interface IUpdatePhoneNumberModalProps {
  headerTitle?: string;
  headerSubTitle?: string;
  preventModalClose?: boolean;
}

export const phoneNumberSentFlagAtom = atomWithStorage<boolean>(
  'phoneNumberSentFlag',
  false,
  createJSONStorage<boolean>(() => localStorage)
);

export const phoneNumberSentAtom = atomWithStorage<string>(
  'phoneNumberSent',
  '',
  createJSONStorage<string>(() => localStorage)
);

export const phoneNumberVerificationDateNowAtom = atomWithStorage<number>(
  'phoneNumberVerificationDateNow',
  null,
  createJSONStorage<number>(() => localStorage)
);

export const phoneNumberVerificationModalVisibleAtom = atom<boolean>(false);

const UpdatePhoneNumberModal: React.FunctionComponent<IUpdatePhoneNumberModalProps> = ({
  headerTitle,
  headerSubTitle,
  preventModalClose,
}) => {
  const [phoneNumberVerificationModalVisible, phoneNumberVerificationModalVisibleToggler] = useAtom(
    phoneNumberVerificationModalVisibleAtom
  );
  const methods = useForm<{
    phoneNumber?: string;
  }>({
    defaultValues: {
      phoneNumber: '',
    },
  });

  const {
    reset,
    control,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
  } = methods;
  const { user } = useAuth();
  const isWhatsappVerification = useIsWhatsappVerification();
  const handleReset = React.useCallback(() => {
    if (preventModalClose) return;
    reset({ phoneNumber: '' });
    phoneNumberVerificationModalVisibleToggler(false);
  }, [phoneNumberVerificationModalVisibleToggler, preventModalClose, reset]);
  const { t } = useTranslation('common');

  const { mutate, isPending, isError, error, isSuccess } = useEmployerPhoneNumberUpdateMutation();
  const onSubmit: SubmitHandler<{ phoneNumber: string }> = React.useCallback((data) => mutate(data), [mutate]);

  const modalProps: IFormModalWrapper = React.useMemo(() => {
    return {
      modalVisible: phoneNumberVerificationModalVisible,
      onModalClose: handleReset,
      title: isWhatsappVerification ? t('verify_whatsapp_number') : headerTitle || t('update_phone_number'),
      subTitle: isWhatsappVerification
        ? t('we_send_a_whatsapp_verification_code')
        : headerSubTitle || t('we_send_a_verification_code'),
      submitHandlerProps: {
        onClick: handleSubmit(onSubmit),
        title: t('send_verification_code'),
        loading: isPending,
      },
      onCancelHandler: handleReset,
      onCancelHandlerProps: {
        disabled: preventModalClose,
      },
      showCloseIcon: !preventModalClose,
      showMobileGestures: !preventModalClose,
      disableDrag: preventModalClose,
    };
  }, [
    handleReset,
    handleSubmit,
    headerSubTitle,
    headerTitle,
    isPending,
    isWhatsappVerification,
    onSubmit,
    phoneNumberVerificationModalVisible,
    preventModalClose,
    t,
  ]);

  React.useEffect(() => {
    if (isError) {
      setError('phoneNumber', {
        message: error?.['data']?.['error'] || t('something_went_wrong'),
      });
    }
  }, [error, isError, setError, t]);

  React.useEffect(() => {
    if (isSuccess) handleReset();
  }, [handleReset, isSuccess]);

  React.useEffect(() => {
    if (user?.phone_number) setValue('phoneNumber', user.phone_number);
  }, [setValue, user?.phone_number]);

  return (
    <ModalToggleWrapper {...modalProps}>
      <FormLabelWrapper
        label={isWhatsappVerification ? t('whatsapp_phone_number') : t('phone_number')}
        isFullWidth
        id="phoneNumber"
      >
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            validate: (phoneNumber) => isValidPhoneNumber(phoneNumber) || t('insert_valid_phone_number'),
          }}
          render={({ field }) => (
            <PhoneInput
              id="phoneNumber"
              countriesMenu={{ favorites: ['IT', 'FR', 'ES'] }}
              textInput={{
                isError: !!errors['phoneNumber'],
                supportText: errors['phoneNumber']?.message as string,
                supportTextVariant: errors['phoneNumber'] ? 'error' : 'default',
                bordercolor: 'neutral-100',
                isFullwidth: true,
              }}
              {...field}
            />
          )}
        />
      </FormLabelWrapper>
    </ModalToggleWrapper>
  );
};

export default UpdatePhoneNumberModal;

export const useShouldShowPhoneNumberVerificationModal = () => {
  const { user } = useAuth();
  const phoneNumberValid = user?.phone_number_valid;
  const phoneNumberVerified = user?.phone_number_verified;
  const phoneNumberDoesNotExist = !user?.phone_number;

  const shouldShowPhoneNumberVerificationModal = React.useMemo(() => {
    if (phoneNumberVerified || (phoneNumberValid && !phoneNumberDoesNotExist)) return false;
    return true;
  }, [phoneNumberDoesNotExist, phoneNumberValid, phoneNumberVerified]);

  return {
    phoneNumberValid,
    phoneNumberVerified,
    phoneNumberDoesNotExist,
    shouldShowPhoneNumberVerificationModal,
  };
};
